import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AudioLines, Download, Ellipsis, Pause, Play } from 'lucide-react'
import { useVoiceStore } from '@/store/voice'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { billingApi } from '@/lib/billingApi'
import { P, basePermissions, has } from '@/lib/permissions'
import { clock, decodeWaveform } from '@/lib/voiceMessage'
import type { Attachment, Guild } from '@/types/api'
import { ContextMenu, MenuItem } from '../ContextMenu'
import { SoundForm } from '../voice/SoundForm'
import styles from './VoicePlayer.module.css'

const BARS = 48
/** Столбиков не меньше этого — на самой узкой плашке волна всё ещё читается. */
const MIN_BARS = 12
const RATES = [1, 1.5, 2]

// играет одно голосовое за раз: новое ставит на паузу предыдущее
let current: HTMLAudioElement | null = null

/** Волна из сохранённых значений, приведённая к count столбикам. */
function bars(levels: number[], count: number): number[] {
  if (levels.length === 0) return Array.from({ length: count }, () => 0.25)
  return Array.from({ length: count }, (_, i) => {
    const from = Math.floor((i * levels.length) / count)
    const to = Math.max(from + 1, Math.floor(((i + 1) * levels.length) / count))
    let peak = 0
    for (let j = from; j < to && j < levels.length; j++) peak = Math.max(peak, levels[j]!)
    return peak
  })
}

/** Слотов звуков по уровням, пока таблица не пришла, — как у нулевого уровня (lib/guildLimits). */
const BASE_SOUNDS = 8

/**
 * Куда можно добавить звук из голосового: серверы, где есть право добавлять звуки и свободное место.
 * Первым — сервер открытого канала, если он подходит.
 */
function useSoundGuilds(): Guild[] {
  const me = useAuthStore((s) => s.user)
  const guilds = useGuildStore((s) => s.guilds)
  const members = useGuildStore((s) => s.members)
  const view = useAppStore((s) => s.view)
  const { data } = useQuery({ queryKey: ['billing-showcase'], queryFn: billingApi.showcase, staleTime: 60 * 60_000 })
  return useMemo(() => {
    if (!me) return []
    const ok = Object.values(guilds).filter((g) => {
      const self = members[g.id]?.[me.id] ?? { roles: [], user: { id: me.id } }
      const perms = basePermissions(g, self)
      const slots = data?.tiers[Math.max(0, Math.min(3, g.premium_tier))]?.sounds ?? BASE_SOUNDS
      return (has(perms, P.CREATE_EXPRESSIONS) || has(perms, P.MANAGE_GUILD_EXPRESSIONS)) && (g.soundboard_sounds?.length ?? 0) < slots
    })
    const here = view.kind === 'guild' ? view.guildId : null
    return ok.sort((a, b) => Number(b.id === here) - Number(a.id === here))
  }, [me, guilds, members, view, data])
}

/** Ссылка, по которой файл скачивается, а не открывается в браузере (сервер ставит attachment). */
function downloadUrl(url: string): string {
  const u = new URL(url, window.location.href)
  u.searchParams.set('download', '1')
  return u.toString()
}

/**
 * Голосовое в сообщении: кнопка, волна с прогрессом (по ней можно перемотать), время и скорость.
 * Звук идёт в выбранное устройство вывода. Если браузер формат не играет — ссылка на файл.
 */
export function VoicePlayer({ attachment }: { attachment: Attachment }) {
  const audio = useRef<HTMLAudioElement>(null)
  const output = useVoiceStore((s) => s.prefs.audiooutput)
  const [playing, setPlaying] = useState(false)
  const [time, setTime] = useState(0)
  const [rate, setRate] = useState(1)
  const [failed, setFailed] = useState(() => typeof document !== 'undefined' && document.createElement('audio').canPlayType(attachment.content_type) === '')
  const waveBox = useRef<HTMLDivElement>(null)
  const [count, setCount] = useState(BARS)
  const levels = useMemo(() => decodeWaveform(attachment.waveform), [attachment.waveform])
  const wave = useMemo(() => bars(levels, count), [levels, count])
  const duration = attachment.duration_secs ?? 0
  const soundGuilds = useSoundGuilds()
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const [soundFile, setSoundFile] = useState<File | null>(null)
  const [soundBusy, setSoundBusy] = useState(false)

  const download = () => {
    const a = document.createElement('a')
    a.href = downloadUrl(attachment.url)
    a.download = attachment.filename
    a.click()
  }
  // голосовое — в звуковую панель: тот же файл открывается в окне звука, где его можно обрезать до 5 секунд
  const toSound = async () => {
    setSoundBusy(true)
    try {
      const blob = await fetch(attachment.url).then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      // имя файла станет названием звука: «voice-message» ни о чём не говорит
      const ext = attachment.filename.includes('.') ? attachment.filename.split('.').pop() : 'ogg'
      setSoundFile(new File([blob], `Голосовое.${ext}`, { type: blob.type || attachment.content_type }))
    } catch {
      setFailed(true)
    } finally {
      setSoundBusy(false)
    }
  }

  // Столбиков столько, сколько влезает (2 px и зазор 2 px): на телефоне, да ещё в пересланном,
  // 48 столбиков не помещались и заезжали под время и скорость.
  useEffect(() => {
    const el = waveBox.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0
      if (width > 0) setCount(Math.max(MIN_BARS, Math.min(BARS, Math.floor((width + 2) / 4))))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [failed])

  useEffect(() => {
    const el = audio.current as (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null
    if (el?.setSinkId && output) void el.setSinkId(output).catch(() => undefined)
  }, [output])

  useEffect(() => () => {
    if (current === audio.current) current = null
  }, [])

  const toggle = () => {
    const el = audio.current
    if (!el) return
    if (!el.paused) {
      el.pause()
      return
    }
    if (current && current !== el) current.pause()
    current = el
    el.playbackRate = rate
    void el.play().catch(() => setFailed(true))
  }

  const seekTo = (fraction: number) => {
    const el = audio.current
    const total = el?.duration && Number.isFinite(el.duration) ? el.duration : duration
    if (!el || !total) return
    el.currentTime = Math.max(0, Math.min(total, fraction * total))
    setTime(el.currentTime)
  }

  const onWaveClick = (e: MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    seekTo((e.clientX - r.left) / r.width)
  }

  const onWaveKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    const el = audio.current
    if (!el || !duration) return
    seekTo((el.currentTime + (e.key === 'ArrowRight' ? 5 : -5)) / duration)
  }

  const cycleRate = () => {
    const next = RATES[(RATES.indexOf(rate) + 1) % RATES.length] ?? 1
    setRate(next)
    if (audio.current) audio.current.playbackRate = next
  }

  if (failed) {
    return (
      <div className={styles.player}>
        <span className={styles.fallback}>Голосовое {duration ? `(${clock(duration)}) ` : ''}не играет в этом браузере</span>
        <a className={styles.download} href={attachment.url} download={attachment.filename}><Download size={16} aria-hidden="true" /> Скачать</a>
      </div>
    )
  }
  const progress = duration ? Math.min(1, time / duration) : 0
  return (
    <div className={styles.player}>
      <button type="button" className={styles.play} aria-label={playing ? 'Пауза' : 'Слушать голосовое'} onClick={toggle}>
        {playing ? <Pause size={18} /> : <Play size={18} />}
      </button>
      <div
        ref={waveBox}
        className={styles.wave}
        role="slider"
        data-message-swipe-surface
        tabIndex={0}
        aria-label="Позиция в голосовом"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(time)}
        aria-valuetext={`${clock(time)} из ${clock(duration)}`}
        onClick={onWaveClick}
        onKeyDown={onWaveKey}
      >
        {wave.map((v, i) => <span key={i} className={(i + 0.5) / wave.length <= progress ? styles.on : undefined} style={{ height: `${Math.max(12, Math.round(v * 100))}%` }} />)}
      </div>
      <span className={styles.time}>{clock(playing || time > 0 ? time : duration)}</span>
      <button type="button" className={styles.rate} data-tooltip="Скорость" aria-label={`Скорость ${rate}×`} onClick={cycleRate}>{String(rate).replace('.', ',')}×</button>
      <button
        type="button"
        className={styles.more}
        data-tooltip="Скачать или сделать звуком"
        aria-label="Ещё"
        aria-haspopup="menu"
        aria-expanded={Boolean(menu)}
        disabled={soundBusy}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect()
          setMenu(menu ? null : { x: r.right, y: r.bottom + 4 })
        }}
      >
        <Ellipsis size={18} />
      </button>
      {menu ? (
        <ContextMenu x={menu.x} y={menu.y} align="right" label="Голосовое" onClose={() => setMenu(null)}>
          <MenuItem icon={<Download size={16} />} label="Скачать" onClick={() => { setMenu(null); download() }} />
          {soundGuilds.length ? (
            <MenuItem icon={<AudioLines size={16} />} label="Сделать звуком панели" onClick={() => { setMenu(null); void toSound() }} />
          ) : null}
        </ContextMenu>
      ) : null}
      {soundFile && soundGuilds[0] ? <SoundForm guild={soundGuilds[0]} guilds={soundGuilds} file={soundFile} onClose={() => setSoundFile(null)} /> : null}
      <audio
        ref={audio}
        src={attachment.url}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setTime(0) }}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onError={() => setFailed(true)}
      />
    </div>
  )
}
